import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { InsurerDataLogsAlert, useBreadcrumb } from '../../../../components';
import { TranslationKeys } from '../../../../translations';
import { Box, Divider, Paper, Stack } from '@mui/material';
import type { PaginationProps } from 'app/components';
import { ComparisonEventsList, ComparisonInsurerData, ComparisonStatusOverview, ComparisonToolBar } from './components';
import {
  useArchiveEventsFromComparisonMutation,
  useBaselineMeasurementsGetComparisonQuery,
  useComparisonCompleteMutation,
  useComparisonItemsQuery,
  useDeclineEventsFromComparisonMutation,
  useProcessEventsFromComparisonMutation,
  useTaskPolling,
} from '../../../../hooks/api/baseline-measurements';
import { useConnectionSearchLogsQuery, useSnakeBar } from '../../../../hooks';
import { ComparisonItemType } from '../../../../types/BaselineMeasurements';
import { InsurerDataRequester } from '../../../../types';
import { baselineMeasurementService } from '../../Services';
import { saveFileFromResponse } from '../../../../utils/FileSaver';
import { useIntl } from 'react-intl';

type Filters = {
  searchQuery: string;
  itemTypes: string[];
  page: PaginationProps['page'];
  pageSize: PaginationProps['rowsPerPage'];
};

type FiltersMap = Map<keyof Filters, Filters[keyof Filters]>;

type BaselineMeasurementParams = {
  employerId: string;
  connectionId: string;
  comparisonId: string;
};

export const BaselineMeasurementPage = () => {
  useBreadcrumb(TranslationKeys.baseline_connection_process_comparison);
  const navigate = useNavigate();
  const intl = useIntl();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();
  const params = useParams<BaselineMeasurementParams>();
  React.useEffect(() => {
    if (!params.employerId || !params.comparisonId || !params.connectionId) {
      navigate('/error', { replace: true });
    }
  }, [params.employerId, params.comparisonId, params.connectionId, navigate]);

  const comparisonId = params.comparisonId as string;
  const connectionId = params.connectionId as string;
  const employerId = params.employerId as string;
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedItems, setSelectedItems] = React.useState<Set<string>>(new Set<string>([]));
  const [filters, setFilters] = React.useState<FiltersMap>(() => {
    const defaultState = new Map<keyof Filters, Filters[keyof Filters]>();
    const urlItemTypes = searchParams.get('itemTypes');
    defaultState.set('searchQuery', '');
    defaultState.set('itemTypes', urlItemTypes ? urlItemTypes.split(',') : []);
    defaultState.set('page', 1);
    defaultState.set('pageSize', 10);
    return defaultState;
  });
  const searchQuery = filters.get('searchQuery') as string;
  const itemTypes = filters.get('itemTypes') as string[];
  const page = filters.get('page') as number;
  const pageSize = filters.get('pageSize') as number;

  const {
    data: comparison,
    isLoading: comparisonIsLoading,
    refetch: refetchComparison,
  } = useBaselineMeasurementsGetComparisonQuery({
    variables: { comparisonId },
    options: {},
  });
  const {
    data: itemsResponse,
    isLoading: itemsLoading,
    refetch: refetchComparisonItemsQuery,
  } = useComparisonItemsQuery({
    variables: {
      connectionId,
      comparisonId,
      searchQuery,
      itemTypes: itemTypes.length > 0 ? (itemTypes as ComparisonItemType[]) : null,
      page,
      pageSize,
    },
    options: {
      enabled: !!connectionId && !!comparisonId,
    },
  });

  const [logsPage, setLogsPage] = React.useState(1);
  const [logsPageSize, setLogsPageSize] = React.useState(10);
  const { data: logsResponse, refetch: refetchComparisonLogsQuery } = useConnectionSearchLogsQuery({
    variables: {
      connectionId,
      requester: InsurerDataRequester.BaselineComparison,
      correlationId: comparison?.insurerDataRequestId,
      page: logsPage,
      pageSize: logsPageSize,
    },
  });

  const { mutate: completeComparisonMutation } = useComparisonCompleteMutation();
  const { mutate: archiveEventsMutation } = useArchiveEventsFromComparisonMutation();
  const { mutate: declineEventsMutation } = useDeclineEventsFromComparisonMutation();
  const { mutate: processEventsMutation } = useProcessEventsFromComparisonMutation();
  const { startPolling, isPolling } = useTaskPolling({
    comparisonId,
    maxAttempts: 10,
    pollingInterval: 5000,
    onSuccess: data => {},
    onTimeout: () => {
      showErrorSnakeBar({
        method: 'queryRunningTasks',
        message: 'Synchronization is taking longer than expected. Updating data...',
      });
    },
    onError: error => {
      showErrorSnakeBar({ method: 'queryRunningTasks', message: error.message });
    },
    onPollingEnd: () => {
      refetchComparison();
      refetchComparisonItemsQuery();
      if (logsPage === 1) {
        refetchComparisonLogsQuery();
      } else {
        setLogsPage(1);
      }
    },
  });

  const onSelectItem = React.useCallback((newSelected: Set<string>) => {
    setSelectedItems(new Set(newSelected));
  }, []);

  const onSelectAllItems = React.useCallback(
    (selectAll: boolean) => {
      const items = itemsResponse?.items || [];
      if (selectAll) {
        const selectableItems = items.filter(item => item.itemType !== ComparisonItemType.Unknown);
        const allItemIds = new Set(selectableItems.map(item => item.comparisonItemId));
        setSelectedItems(allItemIds);
      } else {
        setSelectedItems(new Set<string>());
      }
    },
    [itemsResponse?.items],
  );

  const onArchiveEvents = React.useCallback(() => {
    if (connectionId && comparisonId && selectedItems.size > 0) {
      archiveEventsMutation(
        {
          comparisonId,
          connectionId,
          itemsIds: Array.from(selectedItems),
          selectAll: false,
        },
        {
          onSuccess: () => {
            showSuccessSnakeBar({
              method: 'eventsArchived',
              message: intl.formatMessage({
                id: TranslationKeys['baseline_comparison_actions_events_archive-success'],
              }),
            });
            refetchComparisonItemsQuery();
          },
          onError: error => {
            showErrorSnakeBar({ method: 'eventsArchived', message: 'Impossible Archive Events' });
          },
        },
      );
    }
  }, [
    connectionId,
    comparisonId,
    selectedItems,
    refetchComparisonItemsQuery,
    showSuccessSnakeBar,
    showErrorSnakeBar,
    archiveEventsMutation,
    intl,
  ]);

  const onArchiveAllEvents = React.useCallback(() => {
    if (connectionId && comparisonId) {
      archiveEventsMutation(
        {
          connectionId,
          comparisonId,
          itemsIds: [],
          selectAll: true,
        },
        {
          onSuccess: () => {
            showSuccessSnakeBar({
              method: 'eventsArchived',
              message: intl.formatMessage({
                id: TranslationKeys['baseline_comparison_actions_events_archive-success'],
              }),
            });
            refetchComparisonItemsQuery();
          },
          onError: error => {
            showErrorSnakeBar({ method: 'eventsArchived', message: 'Error en archive events' });
          },
        },
      );
    }
  }, [
    connectionId,
    comparisonId,
    refetchComparisonItemsQuery,
    showSuccessSnakeBar,
    showErrorSnakeBar,
    archiveEventsMutation,
    intl,
  ]);

  const onProcessEvents = React.useCallback(() => {
    if (connectionId && comparisonId && selectedItems.size > 0) {
      processEventsMutation(
        {
          connectionId,
          comparisonId,
          itemsIds: Array.from(selectedItems),
          selectAll: false,
        },
        {
          onSuccess: () => {
            showSuccessSnakeBar({
              method: 'eventsProcessed',
              message: intl.formatMessage({
                id: TranslationKeys['baseline_comparison_actions_events_process-success'],
              }),
            });
            refetchComparisonItemsQuery();
          },
          onError: error => {
            showErrorSnakeBar({ method: 'eventsDeclined', message: 'Error on decline events' });
          },
        },
      );
    }
  }, [
    connectionId,
    comparisonId,
    selectedItems,
    refetchComparisonItemsQuery,
    showSuccessSnakeBar,
    showErrorSnakeBar,
    processEventsMutation,
    intl,
  ]);
  const onProcessAllEvents = React.useCallback(() => {
    if (connectionId && comparisonId) {
      processEventsMutation(
        {
          connectionId,
          comparisonId,
          itemsIds: [],
          selectAll: true,
        },
        {
          onSuccess: () => {
            showSuccessSnakeBar({
              method: 'eventsDeclined',
              message: intl.formatMessage({
                id: TranslationKeys['baseline_comparison_actions_events_process-success'],
              }),
            });
            refetchComparisonItemsQuery();
          },
          onError: error => {
            showErrorSnakeBar({ method: 'eventsDeclined', message: 'Error on decline events' });
          },
        },
      );
    }
  }, [
    connectionId,
    comparisonId,
    refetchComparisonItemsQuery,
    showSuccessSnakeBar,
    showErrorSnakeBar,
    processEventsMutation,
    intl,
  ]);

  const onDeclineEvents = React.useCallback(() => {
    if (connectionId && comparisonId && selectedItems.size > 0) {
      declineEventsMutation(
        {
          connectionId,
          comparisonId,
          itemsIds: Array.from(selectedItems),
          selectAll: false,
        },
        {
          onSuccess: () => {
            showSuccessSnakeBar({
              method: 'eventsDeclined',
              message: intl.formatMessage({
                id: TranslationKeys['baseline_comparison_actions_events_decline-success'],
              }),
            });
            refetchComparisonItemsQuery();
          },
          onError: error => {
            showErrorSnakeBar({ method: 'eventsDeclined', message: 'Error on decline events' });
          },
        },
      );
    }
  }, [
    connectionId,
    comparisonId,
    selectedItems,
    refetchComparisonItemsQuery,
    showSuccessSnakeBar,
    showErrorSnakeBar,
    declineEventsMutation,
    intl,
  ]);
  const onDeclineAllEvents = React.useCallback(() => {
    if (connectionId && comparisonId) {
      declineEventsMutation(
        {
          connectionId,
          comparisonId,
          itemsIds: [],
          selectAll: true,
        },
        {
          onSuccess: () => {
            showSuccessSnakeBar({
              method: 'eventsDeclined',
              message: intl.formatMessage({
                id: TranslationKeys['baseline_comparison_actions_events_decline-success'],
              }),
            });
            refetchComparisonItemsQuery();
          },
          onError: error => {
            showErrorSnakeBar({ method: 'eventsDeclined', message: 'Error on decline events' });
          },
        },
      );
    }
  }, [
    connectionId,
    comparisonId,
    refetchComparisonItemsQuery,
    showSuccessSnakeBar,
    showErrorSnakeBar,
    declineEventsMutation,
    intl,
  ]);

  const onMarkAsComplete = React.useCallback(() => {
    if (!comparisonId) {
      return;
    }
    completeComparisonMutation(
      { comparisonId: comparisonId },
      {
        onSuccess: () => {
          showSuccessSnakeBar({
            method: 'comparisonCompleted',
            message: intl.formatMessage({
              id: TranslationKeys['baseline_comparison_actions_complete_ok'],
            }),
          });
          navigate(`/employers/${employerId}/connections/${connectionId}/events`);
        },
        onError: error => {
          // eslint-disable-next-line no-console
          console.error(error);
          showErrorSnakeBar({
            method: 'comparisonCompleted',
            message: intl.formatMessage({
              id: TranslationKeys['baseline_comparison_actions_complete_ko'],
            }),
          });
        },
      },
    );
  }, [
    comparisonId,
    completeComparisonMutation,
    showSuccessSnakeBar,
    navigate,
    employerId,
    connectionId,
    showErrorSnakeBar,
    intl,
  ]);

  const onFilterItemTypes = React.useCallback(
    (selectedTypes: string[]) => {
      filters.set('itemTypes', selectedTypes);
      filters.set('page', 1);
      setFilters(new Map(filters));
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (selectedTypes.length > 0) {
            next.set('itemTypes', selectedTypes.join(','));
          } else {
            next.delete('itemTypes');
          }
          return next;
        },
        { replace: true },
      );
    },
    [filters, setSearchParams],
  );

  const onDownloadSelected = React.useCallback(() => {
    if (!connectionId || !comparisonId || selectedItems.size === 0) {
      return;
    }
    baselineMeasurementService
      .downloadComparisonItems({
        comparisonId,
        connectionId,
        itemsIds: Array.from(selectedItems),
        selectAll: false,
        itemTypes: itemTypes.length > 0 ? (itemTypes as ComparisonItemType[]) : null,
        searchQuery: searchQuery || null,
      })
      .then(response => {
        saveFileFromResponse(response);
      })
      .catch(() => {
        showErrorSnakeBar({
          method: 'downloadComparisonItems',
          message: intl.formatMessage({ id: TranslationKeys['baseline_comparison_actions_events_download-error'] }),
        });
      });
  }, [connectionId, comparisonId, selectedItems, itemTypes, searchQuery, showErrorSnakeBar, intl]);

  const onDownloadAll = React.useCallback(() => {
    if (!connectionId || !comparisonId) {
      return;
    }
    baselineMeasurementService
      .downloadComparisonItems({
        comparisonId,
        connectionId,
        itemsIds: [],
        selectAll: true,
        itemTypes: itemTypes.length > 0 ? (itemTypes as ComparisonItemType[]) : null,
        searchQuery: searchQuery || null,
      })
      .then(response => {
        saveFileFromResponse(response);
      })
      .catch(() => {
        showErrorSnakeBar({
          method: 'downloadComparisonItems',
          message: intl.formatMessage({ id: TranslationKeys['baseline_comparison_actions_events_download-error'] }),
        });
      });
  }, [connectionId, comparisonId, itemTypes, searchQuery, showErrorSnakeBar, intl]);

  const onSearch = React.useCallback(
    (inputValue: string) => {
      filters.set('searchQuery', inputValue);
      filters.set('page', 1);
      setFilters(new Map(filters));
    },
    [filters],
  );

  const onPaginationChange = React.useCallback<NonNullable<PaginationProps['onPaginationChange']>>(
    ({ page, rowSize }) => {
      if (page !== undefined) {
        filters.set('page', page);
      }
      if (rowSize !== undefined) {
        filters.set('pageSize', rowSize);
        filters.set('page', 1);
      }

      setFilters(new Map(filters));
    },
    [filters],
  );

  const paginationProps = React.useMemo<
    Pick<PaginationProps, 'page' | 'rowsPerPage' | 'count' | 'onPaginationChange'>
  >(() => {
    return {
      page,
      rowsPerPage: pageSize,
      onPaginationChange,
      count: itemsResponse?.totalItems,
    };
  }, [itemsResponse?.totalItems, onPaginationChange, page, pageSize]);

  const onLogsPaginationChange = React.useCallback<NonNullable<PaginationProps['onPaginationChange']>>(
    ({ page, rowSize }) => {
      if (page !== undefined) {
        setLogsPage(page);
      }
      if (rowSize !== undefined) {
        setLogsPageSize(rowSize);
        setLogsPage(1);
      }
    },
    [],
  );

  const logsPaginationProps = React.useMemo<PaginationProps>(
    () => ({
      page: logsPage,
      rowsPerPage: logsPageSize,
      onPaginationChange: onLogsPaginationChange,
      count: logsResponse?.totalItems,
    }),
    [logsPage, logsPageSize, onLogsPaginationChange, logsResponse?.totalItems],
  );

  const onSyncWithInsurer = React.useCallback(() => {
    setTimeout(() => {
      startPolling();
    }, 500); // 1 segundo de delay
  }, [startPolling]);

  const onUploadFileSuccess = React.useCallback(() => {
    setTimeout(() => {
      startPolling();
    }, 500);
  }, [startPolling]);

  const items = itemsResponse?.items || [];

  return (
    <Box sx={{ pt: 2 }}>
      <Paper sx={{ p: 1, mb: 1, borderRadius: 2, bgcolor: '#E4F4F9' }}>
        <Stack spacing={2}>
          <ComparisonStatusOverview
            comparisonData={comparison}
            isLoading={itemsLoading || comparisonIsLoading || isPolling}
            markAsComplete={onMarkAsComplete}
          />

          <Divider sx={{ borderColor: '#c5d9e299', borderWidth: 0.5 }} />

          <ComparisonInsurerData
            comparisonId={comparisonId}
            connectionId={connectionId}
            onUploadSuccess={onUploadFileSuccess}
            onSyncWithInsurerRequested={onSyncWithInsurer}
            loading={itemsLoading || comparisonIsLoading || isPolling}
          />

          <InsurerDataLogsAlert
            testId={'comparison-logs'}
            logs={logsResponse?.items || []}
            totalItems={logsResponse?.totalItems || 0}
            paginationProps={logsPaginationProps}
          />
        </Stack>
      </Paper>

      <ComparisonToolBar
        selectedItemTypes={itemTypes}
        onFilterItemTypes={onFilterItemTypes}
        archiveEvents={onArchiveEvents}
        archiveAllEvents={onArchiveAllEvents}
        processEvents={onProcessEvents}
        processAllEvents={onProcessAllEvents}
        declineEvents={onDeclineEvents}
        declineAllEvents={onDeclineAllEvents}
        downloadSelected={onDownloadSelected}
        downloadAll={onDownloadAll}
        hasSelectedItems={selectedItems.size > 0}
        onSearch={onSearch}
        searchQuery={searchQuery}
        isLoading={itemsLoading || comparisonIsLoading || isPolling}
      />
      <ComparisonEventsList
        connectionId={connectionId}
        comparison={comparison}
        items={items}
        selectedItems={selectedItems}
        onSelectItem={onSelectItem}
        onSelectAllItems={onSelectAllItems}
        paginationProps={paginationProps}
        loading={itemsLoading || comparisonIsLoading || isPolling}
      />
    </Box>
  );
};
